import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { getErrorMessageFromError } from "@jti/fe-common";
import { useDevices, useStartSurvey, useValidateSurveySerialCode } from "@jti/rest-api";
import { Button } from "../../../../components/Button";
import { TextInput } from "../../../../components/Inputs/TextInput";
import { RegisterDeviceDialog } from "../../../../components/RegisterDeviceDialog";
import { useCurrentBreakpoint } from "../../../../hooks/useCurrentBreakpoint";
import { useUser } from "../../../../hooks/useUser";
import { useTranslations } from "../../../../providers/useTranslations";
import { calculateDaysUntilNextWeek } from "../../utils";
import styles from "./SmartSurveyContent.module.scss";
const SURVEY_CODE_FIELD_NAME = "surveyCode";
export const SmartSurveyContent = ({ type, title, ctaText, ctaUrl, subtitle, preCtaText, buttonText, isDisabled, description, availableSurvey, inputPlaceholder, surveyInProgress, surveyMonkeyPageUrl, onRefetchAvailableSurveys, titleAllCompletedRecurringSurveys, subtitleAllCompletedRecurringSurveys, ctaButtonAllCompletedRecurringSurveys, descriptionAllCompletedRecurringSurveys, redirectUrlAllCompletedRecurringSurveys, missingDeviceRegistrationDescription, 
// register device dialog props
theme, requiredCodeLength, registerDeviceImgAlt, registerDeviceImgMobile, registerDeviceImgDesktop, registerDeviceDialogTitle, registerDeviceAccordionTitle, registerDeviceDialogDescription, registerDeviceAccordionButtonUrl, registerDeviceAccordionButtonText, cancelDeviceRegistrationButtonText, registerDeviceAccordionDescription, confirmDeviceRegistrationButtonText, }) => {
    // TODO - remove once user?.has_registered_device is returned from the user API
    useDevices();
    const { specificBreakpoint } = useCurrentBreakpoint();
    const { user } = useUser();
    const { __ } = useTranslations();
    const [isRegisterDevicePopupVisible, setIsRegisterDevicePopupVisible] = useState(false);
    const { isStartSurveyError, isStartSurveyLoading, startSurvey, startSurveyError, resetStartSurvey } = useStartSurvey();
    const { validateSurveySerialCode, resetValidateSurveySerialCode, validateSurveySerialCodeError, isValidateSurveySerialCodeError, isValidateSurveySerialCodeLoading, } = useValidateSurveySerialCode();
    const { control, setValue, formState: { errors }, getValues, handleSubmit, clearErrors, } = useForm({
        defaultValues: {
            [SURVEY_CODE_FIELD_NAME]: "",
        },
    });
    const { is_only_for_device_owners } = surveyInProgress ?? availableSurvey ?? {};
    const isDeviceRegistrationRequired = is_only_for_device_owners && !user?.has_registered_device;
    const isMissingDeviceRegistrationDescriptionVisible = isDeviceRegistrationRequired && missingDeviceRegistrationDescription;
    const formErrorMessage = errors?.[SURVEY_CODE_FIELD_NAME]?.message;
    const isSurveyRequestError = isStartSurveyError || isValidateSurveySerialCodeError;
    const apiErrorMessage = getErrorMessageFromError(startSurveyError ?? validateSurveySerialCodeError ?? {}, __("evo.fallback.error"));
    const inputError = isSurveyRequestError ? apiErrorMessage : formErrorMessage;
    const titleToDisplay = useMemo(() => {
        if (isDisabled) {
            return titleAllCompletedRecurringSurveys;
        }
        return title;
    }, [isDisabled, title, titleAllCompletedRecurringSurveys]);
    const subtitleToDisplay = useMemo(() => {
        if (isDisabled) {
            const daysUntilNextSurvey = calculateDaysUntilNextWeek(new Date());
            return `${daysUntilNextSurvey} ${subtitleAllCompletedRecurringSurveys}`;
        }
        return subtitle;
    }, [isDisabled, subtitle, subtitleAllCompletedRecurringSurveys]);
    const descriptionToDisplay = useMemo(() => {
        if (isDisabled) {
            return descriptionAllCompletedRecurringSurveys;
        }
        return description;
    }, [isDisabled, description, descriptionAllCompletedRecurringSurveys]);
    const isLargeScreen = ["large", "largeMedium", "xLarge"].includes(specificBreakpoint);
    const handleOpenDeviceRegistrationPopup = () => {
        setIsRegisterDevicePopupVisible(true);
    };
    const handleCloseDeviceRegistrationPopup = () => {
        onRefetchAvailableSurveys();
        setIsRegisterDevicePopupVisible(false);
    };
    const handleResetForm = () => {
        if (formErrorMessage) {
            clearErrors(SURVEY_CODE_FIELD_NAME);
        }
        if (isStartSurveyError) {
            resetStartSurvey();
        }
        if (isValidateSurveySerialCodeError) {
            resetValidateSurveySerialCode();
        }
    };
    const handleRedirectUser = () => {
        const { survey_id } = surveyInProgress ?? availableSurvey;
        const redirectUrl = `${surveyMonkeyPageUrl}?surveyType=${type}&uniqueCode=${survey_id}`;
        window.location.href = redirectUrl;
    };
    const handleValidateSurveySerialCode = async () => {
        const { surveyCode } = getValues();
        const { survey_id } = availableSurvey;
        await validateSurveySerialCode({
            userUuid: user?.uuid,
            ciamToken: user?.ciam_token,
            survey_id,
            userInputCode: surveyCode,
        });
    };
    const handleStartSurvey = async () => {
        const { surveyCode } = getValues();
        const { survey_id } = availableSurvey;
        try {
            await startSurvey({
                userUuid: user?.uuid,
                ciamToken: user?.ciam_token,
                survey_id,
                userInputCode: surveyCode,
            });
            handleRedirectUser();
            onRefetchAvailableSurveys();
        }
        catch (error) {
            console.error("Error starting survey:", error);
        }
    };
    const handleCheckDeviceRegistrationForSurveyInProgress = () => {
        const { is_only_for_device_owners } = surveyInProgress;
        if (is_only_for_device_owners && !user?.has_registered_device) {
            handleOpenDeviceRegistrationPopup();
            return;
        }
        handleRedirectUser();
    };
    const handleCheckDeviceRegistrationForNewSurvey = async () => {
        const { is_only_for_device_owners } = availableSurvey;
        if (is_only_for_device_owners && !user?.has_registered_device) {
            try {
                await handleValidateSurveySerialCode();
                handleOpenDeviceRegistrationPopup();
            }
            catch (error) {
                console.error("Error validating survey:", error);
            }
            return;
        }
        await handleStartSurvey();
    };
    const onSubmit = handleSubmit(async () => {
        if (surveyInProgress) {
            handleCheckDeviceRegistrationForSurveyInProgress();
            return;
        }
        await handleCheckDeviceRegistrationForNewSurvey();
    });
    const handleDeviceRegistrationSuccess = () => {
        handleCloseDeviceRegistrationPopup();
        if (surveyInProgress) {
            handleRedirectUser();
            return;
        }
        handleStartSurvey();
    };
    useEffect(() => {
        if (surveyInProgress) {
            const { serial_code } = surveyInProgress;
            setValue(SURVEY_CODE_FIELD_NAME, serial_code);
        }
    }, [setValue, surveyInProgress]);
    return (_jsxs("div", { className: styles.container, children: [_jsx("h2", { className: styles.title, children: titleToDisplay }), _jsx("h3", { className: styles.subTitle, children: subtitleToDisplay }), _jsx("p", { className: styles.description, children: descriptionToDisplay }), isMissingDeviceRegistrationDescriptionVisible && (_jsx("p", { className: styles.description, children: missingDeviceRegistrationDescription })), isDisabled ? (_jsx(Button, { variant: "redirect", href: redirectUrlAllCompletedRecurringSurveys, children: ctaButtonAllCompletedRecurringSurveys })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.ctaWrapper, children: [_jsx("p", { className: styles.preCtaText, children: preCtaText }), _jsx(Button, { href: ctaUrl, type: "redirect", color: "tertiary", target: "_blank", children: ctaText })] }), _jsxs("form", { onSubmit: onSubmit, className: styles.surveyCodeForm, children: [_jsx(Controller, { name: SURVEY_CODE_FIELD_NAME, control: control, rules: {
                                    required: { value: true, message: __("validation.field_required") },
                                }, render: ({ field, fieldState: { error } }) => (_jsx(TextInput, { ...field, errorText: inputError, showRequiredAsterisk: true, dataTestId: "surveyCodeInput", placeholder: inputPlaceholder, containerClassName: styles.input, isError: !!error || isStartSurveyError || isValidateSurveySerialCodeError, isHelperTextVisible: !!error || isStartSurveyError || isValidateSurveySerialCodeError, readOnly: isDisabled || isStartSurveyLoading || !!surveyInProgress || isValidateSurveySerialCodeLoading, onFocus: handleResetForm })) }), _jsx(Button, { type: "submit", disabled: isDisabled, className: styles.submitButton, isPending: isStartSurveyLoading || isValidateSurveySerialCodeLoading, size: isLargeScreen ? "medium" : "small", children: buttonText })] }), isRegisterDevicePopupVisible && (_jsx(RegisterDeviceDialog, { theme: theme, isOpened: true, imageAlt: registerDeviceImgAlt, title: registerDeviceDialogTitle, imgMobile: registerDeviceImgMobile, imgDesktop: registerDeviceImgDesktop, requiredCodeLength: requiredCodeLength, description: registerDeviceDialogDescription, cancelButtonText: cancelDeviceRegistrationButtonText, confirmButtonText: confirmDeviceRegistrationButtonText, registerDeviceAccordionTitle: registerDeviceAccordionTitle, registerDeviceAccordionButtonUrl: registerDeviceAccordionButtonUrl, registerDeviceAccordionDescription: registerDeviceAccordionDescription, registerDeviceAccordionButtonText: registerDeviceAccordionButtonText, onClose: handleCloseDeviceRegistrationPopup, onDeviceRegistrationSuccess: handleDeviceRegistrationSuccess }))] }))] }));
};
//# sourceMappingURL=SmartSurveyContent.js.map